import { useEffect, useRef } from 'react';

export const OVERLAY_SELECTORS = [
  '[data-modal-root]',
  '[data-drawer-root]',
  '[data-portal-dropdown="true"]',
  '[data-overlay-container="true"]',
  '[data-scroll-lock-scrollable="true"]',
  '[role="dialog"]',
  '[role="menu"]',
  '.react-datepicker-popper',
  '#crm-datetime-picker-portal',
  'aside[aria-label="Sidebar"]',
].join(', ');

let lockCount = 0;
let previousBodyOverflow;
let previousBodyOverscroll;
let previousHtmlOverflow;
let previousDashboardOverflow;
let previousDashboardOverscroll;
let savedDashboardScrollTop = 0;
let savedWindowScrollY = 0;
let savedWindowScrollX = 0;
let isTouchListenerActive = false;
let touchStartY = 0;
let lockedInnerContainers = [];

export const isMobileOrTablet = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.innerWidth < 1024 ||
    (window.matchMedia && window.matchMedia('(max-width: 1024px)').matches)
  );
};

const handleTouchStart = (e) => {
  if (e.touches && e.touches.length === 1) {
    touchStartY = e.touches[0].clientY;
  }
};

const handleTouchMove = (e) => {
  if (lockCount === 0) return;

  if (e.touches && e.touches.length > 1) {
    if (e.cancelable) e.preventDefault();
    return;
  }

  const target = e.target;
  if (!(target instanceof Element)) {
    if (e.cancelable) e.preventDefault();
    return;
  }

  const overlayContainer = target.closest(OVERLAY_SELECTORS);

  // If outside any overlay, always prevent default (locks background page)
  if (!overlayContainer) {
    if (e.cancelable) {
      e.preventDefault();
    }
    return;
  }

  // Inside an overlay: find the closest scrollable element up to the overlay container
  let scrollableEl = null;
  let curr = target;
  while (curr && curr !== document.body && curr !== document.documentElement) {
    const style = window.getComputedStyle(curr);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      curr.scrollHeight > curr.clientHeight;

    if (canScrollY) {
      scrollableEl = curr;
      break;
    }
    if (curr === overlayContainer) {
      break;
    }
    curr = curr.parentElement;
  }

  // If the overlay content does not need to scroll, prevent touchmove
  if (!scrollableEl) {
    if (e.cancelable) {
      e.preventDefault();
    }
    return;
  }

  // If scrollable, check boundary to prevent scroll chaining to background
  const currentY = e.touches[0].clientY;
  const deltaY = currentY - touchStartY;
  touchStartY = currentY;

  const { scrollTop, scrollHeight, clientHeight } = scrollableEl;

  const isAtTop = scrollTop <= 0;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

  if (isAtTop && deltaY > 0) {
    if (e.cancelable) {
      e.preventDefault();
    }
    return;
  }

  if (isAtBottom && deltaY < 0) {
    if (e.cancelable) {
      e.preventDefault();
    }
  }
};

const handleWheel = (e) => {
  if (lockCount === 0) return;

  const target = e.target;
  if (!(target instanceof Element)) {
    if (e.cancelable) e.preventDefault();
    return;
  }

  const overlayContainer = target.closest(OVERLAY_SELECTORS);

  // If outside any overlay, always prevent default (locks background page)
  if (!overlayContainer) {
    if (e.cancelable) {
      e.preventDefault();
    }
    return;
  }

  // Inside an overlay: find the closest scrollable element up to the overlay container
  let scrollableEl = null;
  let curr = target;
  while (curr && curr !== document.body && curr !== document.documentElement) {
    const style = window.getComputedStyle(curr);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      curr.scrollHeight > curr.clientHeight;

    if (canScrollY) {
      scrollableEl = curr;
      break;
    }
    if (curr === overlayContainer) {
      break;
    }
    curr = curr.parentElement;
  }

  // If the overlay content does not need to scroll, prevent wheel
  if (!scrollableEl) {
    if (e.cancelable) {
      e.preventDefault();
    }
    return;
  }

  // If scrollable, check boundary to prevent scroll chaining to background
  const deltaY = e.deltaY;
  const { scrollTop, scrollHeight, clientHeight } = scrollableEl;

  const isAtTop = scrollTop <= 0;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

  if (isAtTop && deltaY < 0) {
    if (e.cancelable) {
      e.preventDefault();
    }
    return;
  }

  if (isAtBottom && deltaY > 0) {
    if (e.cancelable) {
      e.preventDefault();
    }
  }
};

const attachTouchListeners = () => {
  if (typeof document === 'undefined' || isTouchListenerActive) return;
  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
  document.addEventListener('wheel', handleWheel, { passive: false });
  isTouchListenerActive = true;
};

const removeTouchListeners = () => {
  if (typeof document === 'undefined' || !isTouchListenerActive) return;
  document.removeEventListener('touchstart', handleTouchStart);
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('wheel', handleWheel);
  isTouchListenerActive = false;
};

export const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;

  if (lockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    previousBodyOverscroll = document.body.style.overscrollBehavior;
    previousHtmlOverflow = document.documentElement.style.overflow;

    savedWindowScrollY = window.scrollY || window.pageYOffset || 0;
    savedWindowScrollX = window.scrollX || window.pageXOffset || 0;

    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'contain';
    document.documentElement.style.overflow = 'hidden';

    const dashboardContainer = document.getElementById(
      'dashboard-scroll-container',
    );
    if (dashboardContainer) {
      previousDashboardOverflow = dashboardContainer.style.overflow;
      previousDashboardOverscroll = dashboardContainer.style.overscrollBehavior;
      savedDashboardScrollTop = dashboardContainer.scrollTop;

      dashboardContainer.style.overflow = 'hidden';
      dashboardContainer.style.overscrollBehavior = 'contain';
    }

    // Lock any inner scrollable containers inside #dashboard-scroll-container that are not part of an overlay
    lockedInnerContainers = [];
    const scrollables = document.querySelectorAll(
      '#dashboard-scroll-container [class*="overflow-"], #dashboard-scroll-container [style*="overflow"]',
    );
    scrollables.forEach((el) => {
      if (el.closest(OVERLAY_SELECTORS)) return;
      const style = window.getComputedStyle(el);
      if (
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflow === 'auto' ||
        style.overflow === 'scroll'
      ) {
        lockedInnerContainers.push({
          element: el,
          previousOverflowY: el.style.overflowY,
          previousOverscroll: el.style.overscrollBehavior,
        });
        el.style.overflowY = 'hidden';
        el.style.overscrollBehavior = 'contain';
      }
    });

    attachTouchListeners();
  }
  lockCount += 1;
};

export const unlockBodyScroll = () => {
  if (typeof document === 'undefined' || lockCount === 0) return;

  lockCount -= 1;

  if (lockCount === 0) {
    document.body.style.overflow = previousBodyOverflow || '';
    document.body.style.overscrollBehavior = previousBodyOverscroll || '';
    document.documentElement.style.overflow = previousHtmlOverflow || '';

    previousBodyOverflow = undefined;
    previousBodyOverscroll = undefined;
    previousHtmlOverflow = undefined;

    const dashboardContainer = document.getElementById(
      'dashboard-scroll-container',
    );
    if (dashboardContainer) {
      dashboardContainer.style.overflow = previousDashboardOverflow || '';
      dashboardContainer.style.overscrollBehavior =
        previousDashboardOverscroll || '';
      dashboardContainer.scrollTop = savedDashboardScrollTop;
      previousDashboardOverflow = undefined;
      previousDashboardOverscroll = undefined;
    }

    lockedInnerContainers.forEach(
      ({ element, previousOverflowY, previousOverscroll }) => {
        if (element && element.isConnected) {
          element.style.overflowY = previousOverflowY || '';
          element.style.overscrollBehavior = previousOverscroll || '';
        }
      },
    );
    lockedInnerContainers = [];

    window.scrollTo(savedWindowScrollX, savedWindowScrollY);

    removeTouchListeners();
  }
};

/**
 * Resets any active scroll locks and cleans up inline styles and event listeners.
 * Useful on route changes or unexpected teardown.
 */
export const forceResetBodyScrollLock = () => {
  if (typeof document === 'undefined') return;

  lockCount = 0;
  document.body.style.overflow = previousBodyOverflow || '';
  document.body.style.overscrollBehavior = previousBodyOverscroll || '';
  document.documentElement.style.overflow = previousHtmlOverflow || '';

  previousBodyOverflow = undefined;
  previousBodyOverscroll = undefined;
  previousHtmlOverflow = undefined;

  const dashboardContainer = document.getElementById(
    'dashboard-scroll-container',
  );
  if (dashboardContainer) {
    dashboardContainer.style.overflow = previousDashboardOverflow || '';
    dashboardContainer.style.overscrollBehavior =
      previousDashboardOverscroll || '';
    previousDashboardOverflow = undefined;
    previousDashboardOverscroll = undefined;
  }

  lockedInnerContainers.forEach(
    ({ element, previousOverflowY, previousOverscroll }) => {
      if (element && element.isConnected) {
        element.style.overflowY = previousOverflowY || '';
        element.style.overscrollBehavior = previousOverscroll || '';
      }
    },
  );
  lockedInnerContainers = [];

  removeTouchListeners();
};

/**
 * Prevents the background page from scrolling while an overlay/modal/dropdown is open.
 * Supports reference counting for nested overlays and mobile-only locking.
 *
 * @param {boolean} shouldLock - Whether the lock should be applied
 * @param {object} [options]
 * @param {boolean} [options.mobileOnly=false] - When true, only locks on mobile/tablet viewports (< 1024px)
 */
export const useBodyScrollLock = (shouldLock, options = {}) => {
  const { mobileOnly = false } = options;
  const isLockedRef = useRef(false);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const evaluateLock = () => {
      const active = Boolean(shouldLock && (!mobileOnly || isMobileOrTablet()));
      if (active && !isLockedRef.current) {
        lockBodyScroll();
        isLockedRef.current = true;
      } else if (!active && isLockedRef.current) {
        unlockBodyScroll();
        isLockedRef.current = false;
      }
    };

    evaluateLock();

    let resizeHandler = null;
    if (shouldLock && mobileOnly) {
      resizeHandler = () => evaluateLock();
      window.addEventListener('resize', resizeHandler);
    }

    return () => {
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      if (isLockedRef.current) {
        unlockBodyScroll();
        isLockedRef.current = false;
      }
    };
  }, [shouldLock, mobileOnly]);
};

/**
 * Convenience hook for dropdowns, popovers, and select menus that should
 * only lock background scrolling on mobile/tablet devices (< 1024px).
 */
export const useMobileScrollLock = (shouldLock) => {
  useBodyScrollLock(shouldLock, { mobileOnly: true });
};

export default useBodyScrollLock;
